
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { AdminSubscription, SubscriptionsResponse } from "./types";
import { Json } from "@/integrations/supabase/types";

type FilterFunction = {
  Args: {
    search_query: string | null;
    page_number: number;
    page_size: number;
    status_filter?: string | null;
  };
  Returns: {
    total_count: number;
    subscriptions: Json;
  }[];
}

export function useSubscriptionsData(
  searchQuery: string, 
  currentPage: number, 
  statusFilter: string | null,
  pageSize: number
) {
  const [subscriptions, setSubscriptions] = useState<AdminSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  useEffect(() => {
    if (!mounted) return;
    fetchSubscriptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mounted, currentPage, searchQuery, statusFilter, pageSize]);

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .rpc<'get_filtered_subscriptions', FilterFunction>('get_filtered_subscriptions', {
          search_query: searchQuery || null,
          page_number: currentPage,
          page_size: pageSize,
          status_filter: statusFilter
        })
        .returns<SubscriptionsResponse[]>();

      if (error) throw error;

      if (mounted && data && data[0]) {
        const rawSubs = Array.isArray(data[0].subscriptions) ? data[0].subscriptions : [];
        // Debug log: confirm that each row has school_id
        console.log("Subscriptions from database (should have school_id):", rawSubs);

        setSubscriptions(rawSubs);
        setTotalCount(data[0].total_count || 0);
      } else {
        setSubscriptions([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      setSubscriptions([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  return { subscriptions, loading, totalCount };
}
